import { useState, useEffect, useCallback } from 'react'
import {
  Search,
  Plus,
  Download,
  Eye,
  ChevronLeft,
  ChevronRight,
  Coins,
  History,
  Gift,
  X,
  Edit,
  Lock,
  Unlock,
  AlertCircle,
  Building2
} from 'lucide-react'
import { usersApi, pointsApi, exchangesApi, hospitalsApi } from '../../lib/api'
import { useAuth } from '../../lib/auth'

interface UserItem {
  id: string
  account_id: string
  member_no?: string
  phone: string
  name: string
  id_card?: string
  points: number
  status: string
  created_at: string
  last_active_at?: string
  commitment_signed_at?: string
  tags?: string
  hospital_id?: string
  hospital_name?: string
  crowd_type?: string
  age_group?: string
  role?: string  // user / medical
  medical_role?: string  // staff / supervisor
}

const Users = () => {
  const { user } = useAuth()
  const isSupervisor = user?.role === 'medical' && user?.medicalRole === 'supervisor'
  const [users, setUsers] = useState<UserItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [jumpInput, setJumpInput] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserItem | null>(null)
  const [modalType, setModalType] = useState<'view' | 'points' | 'freeze'>('view')

  const [pointChange, setPointChange] = useState('')
  const [pointReason, setPointReason] = useState('')
  const [pointLoading, setPointLoading] = useState(false)

  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyRecords, setHistoryRecords] = useState<any[]>([])
  const [historyStats, setHistoryStats] = useState({ total: 0, pending: 0, month: 0 })
  const [historyPage, setHistoryPage] = useState(1)
  const [historyHasMore, setHistoryHasMore] = useState(false)
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false)

  const [showExchangeModal, setShowExchangeModal] = useState(false)
  const [exchangeLoading, setExchangeLoading] = useState(false)
  const [exchangeRecords, setExchangeRecords] = useState<any[]>([])

  const [showAddModal, setShowAddModal] = useState(false)
  const [addError, setAddError] = useState('')
  const [addSuccess, setAddSuccess] = useState(false)

  const [showEditModal, setShowEditModal] = useState(false)
  const [editForm, setEditForm] = useState({
    name: '', phone: '', id_card: '', password: '', is_frozen: false,
    hospital_id: '', crowd_type: '', age_group: '', tags: ''
  })
  const [editLoading, setEditLoading] = useState(false)
  const [hospitals, setHospitals] = useState<{id: string, name: string}[]>([])

  // 获取机构列表
  useEffect(() => {
    if (showEditModal) {
      hospitalsApi.list({ status: 'active' }).then((res: any) => {
        setHospitals(res.hospitals || res.items || [])
      }).catch(() => {})
    }
  }, [showEditModal])

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params: Record<string, unknown> = {
        page: currentPage,
        page_size: pageSize,
      }
      if (searchTerm) params.keyword = searchTerm
      if (statusFilter !== 'all') params.status = statusFilter
      // 机构负责人只能查看本机构用户
      if (isSupervisor && user?.hospitalId) {
        params.hospital_id = user.hospitalId
      }

      const res = await usersApi.list(params) as { total: number; users: UserItem[] }
      setUsers(res.users || [])
      setTotal(res.total || 0)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [currentPage, searchTerm, statusFilter, isSupervisor, user?.hospitalId, pageSize])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  // 防抖搜索
  useEffect(() => {
    const t = setTimeout(() => { setCurrentPage(1) }, 400)
    return () => clearTimeout(t)
  }, [searchTerm, statusFilter])

  // 弹窗打开或切换用户时自动加载积分历史
  useEffect(() => {
    if (showHistoryModal && selectedUser?.id) {
      // 检查是否需要重新加载（用户切换或首次加载）
      const isDifferentUser = historyRecords.length > 0 && historyRecords[0]?.user_id !== selectedUser.id
      if (historyRecords.length === 0 || isDifferentUser) {
        setHistoryRecords([])  // 清空旧数据
        handleViewHistory(selectedUser.id, 1)
      }
    }
  }, [showHistoryModal, selectedUser?.id])

  const totalPages = Math.ceil(total / pageSize)

  // 页码数组（最多 7 个按钮 + 省略号）
  const getPageNumbers = (): (number | string)[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const pages: (number | string)[] = [1]
    if (currentPage > 3) pages.push('...')
    const start = Math.max(2, currentPage - 1)
    const end = Math.min(totalPages - 1, currentPage + 1)
    for (let i = start; i <= end; i++) pages.push(i)
    if (currentPage < totalPages - 2) pages.push('...')
    pages.push(totalPages)
    return pages
  }

  const handleJump = () => {
    const p = parseInt(jumpInput)
    if (!isNaN(p) && p >= 1 && p <= totalPages) {
      setCurrentPage(p)
      setJumpInput('')
    }
  }

  const handleOpenModal = (user: UserItem, type: 'view' | 'points' | 'freeze') => {
    setSelectedUser(user)
    setModalType(type)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedUser(null)
    setPointChange('')
    setPointReason('')
  }

  const handleViewHistory = async (userId: string, page: number = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setHistoryLoadingMore(true)
    } else {
      setHistoryLoading(true)
    }
    try {
      const res = await pointsApi.records({ user_id: userId, page, page_size: 20 })
      const data = res as any
      const list = data.records || data.items || []

      if (isLoadMore) {
        setHistoryRecords(prev => [...prev, ...list])
      } else {
        setHistoryRecords(list)
      }
      setHistoryPage(page)
      const totalPages = data.total_pages || 1
      setHistoryHasMore(page < totalPages && list.length >= 20)
      setHistoryStats({
        total: data.total_confirmed || 0,
        pending: data.pending_points || 0,
        month: data.month_confirmed || 0,
      })
    } catch (e) {
      if (!isLoadMore) setHistoryRecords([])
    } finally {
      setHistoryLoading(false)
      setHistoryLoadingMore(false)
    }
  }

  const handleViewExchanges = async (userId: string) => {
    setExchangeLoading(true)
    try {
      const res = await exchangesApi.list({ user_id: userId, page_size: 20 })
      const data = res as any
      const list = data.items || data.exchanges || []
      setExchangeRecords(list)
    } catch (e) {
      setExchangeRecords([])
    } finally {
      setExchangeLoading(false)
    }
  }

  const handleFreeze = async () => {
    if (!selectedUser) return
    try {
      if (selectedUser.status === 'frozen') {
        await usersApi.unfreeze(selectedUser.account_id)
      } else {
        await usersApi.freeze(selectedUser.account_id)
      }
      handleCloseModal()
      fetchUsers()
    } catch (e) {
      alert((e as Error).message)
    }
  }

  const handleAdjustPoints = async () => {
    if (!selectedUser || !pointChange || !pointReason) return
    setPointLoading(true)
    try {
      const delta = parseInt(pointChange)
      if (isNaN(delta)) { alert('请输入有效的积分数值'); return }
      await usersApi.adjustPoints(selectedUser.id, delta, pointReason)
      handleCloseModal()
      fetchUsers()
    } catch (e) {
      alert((e as Error).message)
    } finally {
      setPointLoading(false)
    }
  }

  const handleExport = async () => {
    try {
      const params: Record<string, unknown> = {}
      if (isSupervisor && user?.hospitalId) params.hospital_id = user.hospitalId
      const qs = Object.entries(params).filter(([, v]) => v != null).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')
      const res = await fetch('/api/v1/export/users' + (qs ? `?${qs}` : ''), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      if (!res.ok) throw new Error('导出失败')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `会员管理_${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      alert((e as Error).message)
    }
  }

  const maskPhone = (p: string) => p ? p.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') : '-'
  const maskIdCard = (id: string) => id ? id.replace(/(\d{6})\d{8}(\d{4})/, '$1********$2') : '-'

  // 根据人群类型和年龄返回标签颜色（用于会员编号背景）
  const getMemberNoColor = (tags?: string, crowdType?: string, ageGroup?: string) => {
    // 优先使用 tags 颜色
    if (tags) {
      return 'bg-purple-500 text-white'
    }
    // 无 tags 时，按年龄+人群类型判断
    if (!crowdType) return ''
    if (crowdType === '普通人群') return 'bg-white text-gray-900 border border-gray-300'
    if (ageGroup === '>65') {
      if (crowdType === '慢性病患者') return 'bg-red-500 text-white'
      if (crowdType === '慢性病高危人群') return 'bg-yellow-500 text-white'
    }
    if (ageGroup === '<=65') {
      if (crowdType === '慢性病患者') return 'bg-orange-500 text-white'
      if (crowdType === '慢性病高危人群') return 'bg-green-500 text-white'
    }
    return ''
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">会员管理</h1>
        <div className="flex items-center gap-3">
          <button onClick={handleExport} className="flex items-center gap-1 px-4 py-2 text-sm bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50">
            <Download size={16} /> 导出
          </button>
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-1 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">
            <Plus size={16} /> 添加会员
          </button>
        </div>
      </div>

      {/* 搜索过滤 */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="搜索姓名、手机号、身份证..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg">
          <option value="all">全部状态</option>
          <option value="active">正常</option>
          <option value="frozen">已冻结</option>
        </select>
      </div>

      {/* 统计 */}
      <div className="flex gap-4 text-sm text-gray-500">
        <span>共 <strong>{total}</strong> 位注册会员</span>
        <span>本页 {users.length} 条</span>
      </div>

      {/* 表格 */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">会员编号</th>
              <th className="px-4 py-3 text-left">姓名</th>
              <th className="px-4 py-3 text-left">角色</th>
                            <th className="px-4 py-3 text-left">手机号</th>
              <th className="px-4 py-3 text-right">积分</th>
              <th className="px-4 py-3 text-center">状态</th>
              <th className="px-4 py-3 text-left">绑定机构</th>
              <th className="px-4 py-3 text-left">人群类型</th>
              <th className="px-4 py-3 text-left">年龄段</th>
              <th className="px-4 py-3 text-left">标签</th>
              <th className="px-4 py-3 text-left">注册时间</th>
              <th className="px-4 py-3 text-center">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="text-center py-12 text-gray-400">加载中...</td></tr>
            ) : error ? (
              <tr><td colSpan={12} className="text-center py-12 text-red-500">{error}</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={12} className="text-center py-12 text-gray-400">暂无数据</td></tr>
            ) : users.map(user => {
              const memberNoColor = getMemberNoColor(user.tags, user.crowd_type, user.age_group)
              return (
              <tr key={user.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-3">
                  {user.member_no ? (
                    <span className={`px-2 py-0.5 text-xs rounded font-medium ${memberNoColor || 'bg-green-50 text-green-600'}`}>
                      {user.member_no}
                    </span>
                  ) : '-'}
                </td>
                <td className="px-4 py-3 font-medium">{user.name || '-'}</td>
                <td className="px-4 py-3">
                  {(() => {
                    const roleLabel = user.role === 'medical'
                      ? (user.medical_role === 'supervisor' ? '负责人' : '医护')
                      : '用户'
                    const roleColor = user.role === 'medical'
                      ? (user.medical_role === 'supervisor' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700')
                      : 'bg-green-100 text-green-700'
                    return <span className={`px-2 py-0.5 text-xs rounded font-medium ${roleColor}`}>{roleLabel}</span>
                  })()}
                </td>
                <td className="px-4 py-3">{maskPhone(user.phone || '')}</td>
                <td className="px-4 py-3 text-right">
                  <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                    <Coins size={14} /> {user.points ?? 0}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    user.status === 'frozen' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                  }`}>
                    {user.status === 'frozen' ? '已冻结' : '正常'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {user.hospital_name ? (
                    <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded">
                      <Building2 size={12} className="inline mr-1" />{user.hospital_name}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">未绑定</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-1.5 py-0.5 text-xs rounded ${user.crowd_type === '慢性病患者' ? 'bg-orange-100 text-orange-700' : user.crowd_type === '慢性病高危人群' ? 'bg-green-100 text-green-700' : user.crowd_type === '普通人群' ? 'bg-gray-100 text-gray-600' : user.crowd_type ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'}`}>
                    {user.crowd_type || '-'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-1.5 py-0.5 text-xs rounded ${user.age_group === '>65' ? 'bg-red-100 text-red-700' : user.age_group === '<=65' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                    {user.age_group === '>65' ? '65岁+' : user.age_group === '<=65' ? '65岁-' : '-'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {user.tags ? (
                    <div className="flex flex-wrap gap-1">
                      {user.tags.split(',').map((tag, i) => tag && (
                        <span key={i} className="px-1.5 py-0.5 bg-purple-500 text-white text-xs rounded">{tag}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-xs">暂无</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{user.created_at ? user.created_at.replace('T', ' ').slice(0, 19) : '-'}</td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => handleOpenModal(user, 'view')}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="查看">
                      <Eye size={16} />
                    </button>
                    <button onClick={() => { setSelectedUser(user); setEditForm({ name: user.name || '', phone: user.phone || '', id_card: user.id_card || '', password: '', is_frozen: user.status === 'frozen', hospital_id: user.hospital_id || '', crowd_type: user.crowd_type || '', age_group: user.age_group || '', tags: user.tags || '' }); setShowEditModal(true) }}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="编辑">
                      <Edit size={16} />
                    </button>
                    <button onClick={() => handleOpenModal(user, 'freeze')}
                      className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" title={user.status === 'frozen' ? '解冻' : '冻结'}>
                      {user.status === 'frozen' ? <Unlock size={16} /> : <Lock size={16} />}
                    </button>
                    <button onClick={() => { setSelectedUser(user); setShowHistoryModal(true) }}
                      className="p-1.5 text-purple-600 hover:bg-purple-50 rounded" title="积分历史">
                      <History size={16} />
                    </button>
                    <button onClick={() => { setSelectedUser(user); setShowExchangeModal(true); handleViewExchanges(user.id) }}
                      className="p-1.5 text-pink-600 hover:bg-pink-50 rounded" title="兑换记录">
                      <Gift size={16} />
                    </button>
                    <button onClick={() => handleOpenModal(user, 'points')}
                      className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title="调整积分">
                      <Coins size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            )
            })}
          </tbody>
        </table>
      </div>

      {/* 分页 */}
      {totalPages > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span>共 {total} 位</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1) }}
              className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value={10}>10 条/页</option>
              <option value={20}>20 条/页</option>
              <option value={50}>50 条/页</option>
              <option value={100}>100 条/页</option>
            </select>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors">
                <ChevronLeft size={16} />
              </button>
              {getPageNumbers().map((p, i) =>
                p === '...' ? (
                  <span key={`ellipsis-${i}`} className="px-2 text-gray-400">...</span>
                ) : (
                  <button key={p} onClick={() => setCurrentPage(p as number)}
                    className={`min-w-[36px] px-2 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === p
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}>
                    {p}
                  </button>
                )
              )}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors">
                <ChevronRight size={16} />
              </button>
              <div className="flex items-center gap-1 ml-2">
                <span className="text-sm text-gray-400">跳至</span>
                <input
                  type="text"
                  value={jumpInput}
                  onChange={(e) => setJumpInput(e.target.value.replace(/[^0-9]/g, ''))}
                  onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                  onBlur={handleJump}
                  placeholder={String(currentPage)}
                  className="w-12 border border-gray-200 rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-400">页</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 查看/冻结 弹窗 */}
      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={handleCloseModal}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            {modalType === 'view' && (
              <>
                <h2 className="text-lg font-bold mb-4">用户详情</h2>
                <dl className="space-y-3 text-sm">
                  <div className="flex justify-between"><dt className="text-gray-500">姓名</dt><dd>{selectedUser.name}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">手机号</dt><dd>{selectedUser.phone}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">身份证</dt><dd>{selectedUser.id_card || '-'}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">当前积分</dt><dd className="text-amber-600 font-medium">{selectedUser.points ?? 0}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">状态</dt><dd>
                    <span className={`px-2 py-0.5 rounded text-xs ${selectedUser.status === 'frozen' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                      {selectedUser.status === 'frozen' ? '已冻结' : '正常'}
                    </span>
                  </dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">绑定机构</dt><dd>{selectedUser.hospital_name || selectedUser.hospital_id || '-'}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">人群类型</dt><dd>{selectedUser.crowd_type || '-'}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">年龄范围</dt><dd>{selectedUser.age_group === '>65' ? '65岁及以上' : selectedUser.age_group === '<=65' ? '65岁以下' : '-'}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">标签</dt><dd>
                    {selectedUser.tags ? (
                      <div className="flex flex-wrap gap-1">
                        {selectedUser.tags.split(',').map((tag, i) => tag && (
                          <span key={i} className="px-1.5 py-0.5 bg-purple-500 text-white text-xs rounded">{tag}</span>
                        ))}
                      </div>
                    ) : '-'}
                  </dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">注册时间</dt><dd>{selectedUser.created_at ? selectedUser.created_at.replace('T', ' ').slice(0, 19) : '-'}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">签署承诺书</dt><dd>{selectedUser.commitment_signed_at ? selectedUser.commitment_signed_at.replace('T', ' ').slice(0, 19) : <span className="text-red-500">未签署</span>}</dd></div>
                </dl>
              </>
            )}

            {modalType === 'points' && (
              <>
                <h2 className="text-lg font-bold mb-4">调整积分 - {selectedUser.name}</h2>
                <p className="text-sm text-gray-500 mb-3">当前积分: <strong className="text-amber-600">{selectedUser.points ?? 0}</strong></p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">积分变动</label>
                    <input type="number" value={pointChange} onChange={e => setPointChange(e.target.value)}
                      placeholder="输入数值（如 100 增加，-50 减少）"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">调整原因</label>
                    <textarea value={pointReason} onChange={e => setPointReason(e.target.value)}
                      placeholder="请输入调整原因（必填）"
                      rows={3}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <button onClick={handleAdjustPoints} disabled={pointLoading}
                    className="w-full py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
                    {pointLoading ? '提交中...' : '确认调整'}
                  </button>
                </div>
              </>
            )}

            {modalType === 'freeze' && (
              <>
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <AlertCircle className="text-red-500" size={20} />
                  {selectedUser.status === 'frozen' ? '解冻账号' : '冻结账号'}
                </h2>
                <p className="text-gray-600 mb-4">
                  确定要{selectedUser.status === 'frozen' ? '解冻' : '冻结'}账号 <strong>{selectedUser.name}</strong> 吗？
                  {selectedUser.status !== 'frozen' && ' 冻结后该用户将无法登录。'}
                </p>
                <div className="flex gap-3">
                  <button onClick={handleCloseModal}
                    className="flex-1 py-2 border rounded-lg hover:bg-gray-50">取消</button>
                  <button onClick={handleFreeze}
                    className={`flex-1 py-2 text-white rounded-lg ${selectedUser.status === 'frozen' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
                    确认{selectedUser.status === 'frozen' ? '解冻' : '冻结'}
                  </button>
                </div>
              </>
            )}

            <button onClick={handleCloseModal}
              className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* 编辑用户弹窗 */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowEditModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">编辑用户</h2>
              <button onClick={() => setShowEditModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">姓名</label>
                <input type="text" autoComplete="off" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">手机号</label>
                <input type="text" autoComplete="off" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">身份证</label>
                <input type="text" autoComplete="off" value={editForm.id_card} onChange={e => setEditForm({...editForm, id_card: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="选填" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
                <input type="password" autoComplete="new-password" value={editForm.password} onChange={e => setEditForm({...editForm, password: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="不修改请留空" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_frozen" checked={editForm.is_frozen} onChange={e => setEditForm({...editForm, is_frozen: e.target.checked})}
                  className="w-4 h-4 rounded" />
                <label htmlFor="is_frozen" className="text-sm text-gray-700">冻结账号</label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">绑定机构</label>
                <select value={editForm.hospital_id} onChange={e => setEditForm({...editForm, hospital_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">请选择机构</option>
                  {hospitals.map(h => (
                    <option key={h.id} value={h.id}>{h.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">人群类型</label>
                <select value={editForm.crowd_type} onChange={e => setEditForm({...editForm, crowd_type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">请选择人群类型</option>
                  <option value="慢性病患者">慢性病患者</option>
                  <option value="慢性病高危人群">慢性病高危人群</option>
                  <option value="普通人群">普通人群</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">年龄范围</label>
                <select value={editForm.age_group} onChange={e => setEditForm({...editForm, age_group: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">请选择年龄范围</option>
                  <option value=">65">65岁及以上</option>
                  <option value="<=65">65岁以下</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">标签</label>
                <input type="text" autoComplete="off" value={editForm.tags} onChange={e => setEditForm({...editForm, tags: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="管理员标注" />
                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={() => setEditForm(f => ({ ...f, tags: f.tags ? f.tags + ',重点关注人群' : '重点关注人群' }))}
                    className="px-2 py-1 text-xs bg-purple-100 text-purple-700 border border-purple-200 rounded hover:bg-purple-200">
                    +重点关注人群
                  </button>
                  <button type="button" onClick={() => setEditForm(f => ({ ...f, tags: '' }))}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-600 border border-gray-200 rounded hover:bg-gray-200">
                    清空
                  </button>
                </div>
              </div>
              <button onClick={async () => {
                if (!editForm.name || !editForm.phone) { alert('姓名和手机号不能为空'); return }
                setEditLoading(true)
                try {
                  await usersApi.update(selectedUser.id, {
                    name: editForm.name,
                    phone: editForm.phone,
                    id_card: editForm.id_card || undefined,
                    password: editForm.password || undefined,
                    status: editForm.is_frozen ? 'frozen' : 'active',
                    hospital_id: editForm.hospital_id || undefined,
                    crowd_type: editForm.crowd_type || undefined,
                    age_group: editForm.age_group || undefined,
                    tags: editForm.tags.length > 0 ? editForm.tags : '',
                  })
                  setShowEditModal(false)
                  fetchUsers()
                } catch (e) { alert('修改失败: ' + (e as Error).message) }
                finally { setEditLoading(false) }
              }}
                disabled={editLoading}
                className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                {editLoading ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 添加用户弹窗 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">添加会员</h2>
              <button onClick={() => setShowAddModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              {addError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  {addError}
                </div>
              )}
              {addSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-600">
                  添加成功！
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">姓名 <span className="text-red-500">*</span></label>
                <input type="text" id="add-name" placeholder="请输入姓名" className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">手机号 <span className="text-red-500">*</span></label>
                <input type="tel" id="add-phone" placeholder="请输入手机号" className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">身份证</label>
                <input type="text" id="add-idcard" placeholder="请输入身份证号" className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">登录密码 <span className="text-red-500">*</span></label>
                <input type="password" id="add-password" placeholder="请设置登录密码（至少8位，包含大小写字母和数字）" className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <button
                onClick={async () => {
                  const name = (document.getElementById('add-name') as HTMLInputElement).value.trim()
                  const phone = (document.getElementById('add-phone') as HTMLInputElement).value.trim()
                  const id_card = (document.getElementById('add-idcard') as HTMLInputElement).value.trim()
                  const password = (document.getElementById('add-password') as HTMLInputElement).value

                  if (!name || !phone || !password) {
                    setAddError('请填写必填项（姓名、手机号、密码）')
                    return
                  }
                  if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/.test(password)) {
                    setAddError('密码强度不足，需至少8位，包含大小写字母和数字')
                    return
                  }

                  setAddError('')
                  setAddSuccess(false)
                  try {
                    await usersApi.create({ name, phone, password, id_card: id_card || undefined })
                    setAddSuccess(true)
                    setTimeout(() => {
                      setShowAddModal(false)
                      setAddSuccess(false)
                      setAddError('')
                      fetchUsers()
                    }, 1000)
                  } catch (e) {
                    setAddError((e as Error).message || '添加失败')
                  }
                }}
                className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                确认添加
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 积分历史弹窗 */}
      {showHistoryModal && selectedUser && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowHistoryModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{selectedUser.name} - 积分历史</h2>
              <button onClick={() => setShowHistoryModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="flex gap-4 mb-4">
              <div className="flex-1 bg-green-50 rounded-lg p-3 text-center">
                <div className="text-xs text-green-600">累计获得</div>
                <div className="text-lg font-bold text-green-700">{historyStats.total}</div>
              </div>
              <div className="flex-1 bg-orange-50 rounded-lg p-3 text-center">
                <div className="text-xs text-orange-600">待确认</div>
                <div className="text-lg font-bold text-orange-700">{historyStats.pending}</div>
              </div>
              <div className="flex-1 bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-xs text-blue-600">本月获得</div>
                <div className="text-lg font-bold text-blue-700">{historyStats.month}</div>
              </div>
            </div>
            {historyLoading ? (
              <div className="flex-1 flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-green-600 border-t-transparent rounded-full" />
              </div>
            ) : historyRecords.length === 0 ? (
              <div className="text-center py-8 text-gray-400">暂无积分记录</div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-600">时间</th>
                      <th className="px-3 py-2 text-left text-gray-600">事件</th>
                      <th className="px-3 py-2 text-right text-gray-600">积分</th>
                      <th className="px-3 py-2 text-left text-gray-600">状态</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {historyRecords.map(r => (
                      <tr key={r.id}>
                        <td className="px-3 py-2 text-gray-500 text-xs">{r.created_at?.slice(0, 16)}</td>
                        <td className="px-3 py-2">
                          <div>{r.event_type_name || '-'}</div>
                          {r.remark && (
                            <div className="text-xs text-orange-500 mt-0.5">备注：{r.remark}</div>
                          )}
                          {r.cancel_reason && (
                            <div className="text-xs text-red-500 mt-0.5">撤销原因：{r.cancel_reason}</div>
                          )}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${r.points > 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {r.points > 0 ? '+' : ''}{r.points}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 text-xs rounded ${r.status === 'confirmed' ? 'bg-green-100 text-green-700' : r.status === 'cancelled' ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-600'}`}>
                            {r.status === 'confirmed' ? '已确认' : r.status === 'cancelled' ? '已撤销' : '待确认'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {historyHasMore && !historyLoading && !historyLoadingMore && (
                  <button onClick={() => handleViewHistory(selectedUser.id, historyPage + 1, true)}
                    className="w-full py-2 text-center text-green-600 text-sm hover:bg-gray-50">
                    加载更多
                  </button>
                )}
                {historyLoadingMore && (
                  <div className="py-2 text-center text-gray-500 text-sm">加载中...</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 兑换记录弹窗 */}
      {showExchangeModal && selectedUser && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowExchangeModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{selectedUser.name} - 兑换记录</h2>
              <button onClick={() => setShowExchangeModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            {exchangeLoading ? (
              <div className="flex-1 flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-green-600 border-t-transparent rounded-full" />
              </div>
            ) : exchangeRecords.length === 0 ? (
              <div className="text-center py-8 text-gray-400">暂无兑换记录</div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-600">时间</th>
                      <th className="px-3 py-2 text-left text-gray-600">礼品</th>
                      <th className="px-3 py-2 text-right text-gray-600">消耗积分</th>
                      <th className="px-3 py-2 text-left text-gray-600">状态</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {exchangeRecords.map(r => (
                      <tr key={r.id}>
                        <td className="px-3 py-2 text-gray-500 text-xs">{r.created_at?.slice(0, 16)}</td>
                        <td className="px-3 py-2">{r.gift_name || '-'}</td>
                        <td className="px-3 py-2 text-right font-medium text-red-500">-{r.points}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 text-xs rounded ${r.status === 'confirmed' ? 'bg-green-100 text-green-700' : r.status === 'pending' ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-500'}`}>
                            {r.status === 'confirmed' ? '已完成' : r.status === 'pending' ? '处理中' : '已取消'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Users
